/**
 * 로그인 직후 안내 — 사업자등록증과 결제 수단
 * ---------------------------------------------------------------------------
 * ## 왜 로그인 화면이 아니라 여기인가
 * 요청은 「로그인 화면에 메시지를 띄워 달라」였다. 그런데 로그인 **전에는
 * 누가 묻는지 모른다.** 이메일을 치기 전이면 기업을 특정할 수 없고, 특정할
 * 수 있게 만들면 **아무나 주소창으로 「어느 회사가 결제 수단을 등록하지
 * 않았는지」를 확인할 수 있다.** 그건 알려 줄 값이 아니다.
 *
 * 그래서 **로그인 직후 첫 화면 맨 위**에 띄운다. 보는 사람은 같고(그 기업의
 * 관리자), 보이는 시점도 같고(들어오자마자), 다른 점은 하나다 — 남에게는
 * 보이지 않는다. 할 일을 누르면 바로 그 화면으로 가므로 오히려 가깝다.
 *
 * ## 모든 화면에 띄우지 않는다
 * 자사 현황(첫 화면)에서만 띄운다. 어디서나 따라다니면 사흘이면 아무도
 * 읽지 않는다 — 닫을 수 없는 띠는 그냥 배경이 된다.
 *
 * ## 닫아도 다시 뜬다
 * 접어 둘 수는 있지만 **브라우저를 다시 열면 다시 보인다.** 기한이 걸린
 * 일이라 "닫았으니 끝" 이 되면 안 된다.
 */

import { Alert, Button, Space, Typography } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useOnboarding } from '../features/tenant/onboarding';

const { Text } = Typography;

export function OnboardingBanner() {
  const navigate = useNavigate();
  const { data } = useOnboarding();

  if (!data?.needsAttention) return null;

  const missing: Array<{ what: string; where: string; to: string }> = [];
  if (!data.bizLicenseAttached) {
    missing.push({
      what: '사업자등록증',
      where: '설정 → 기업 설정 → 기업 정보',
      to: '/app/settings',
    });
  }
  if (!data.payMethodRegistered) {
    missing.push({
      what: '결제 수단',
      where: '구독 → 사용량·청구 → 결제 수단',
      to: '/app/billing',
    });
  }

  /*
   * 무료 기간이 끝나 가는데 둘 다 없으면 말을 세게 한다.
   * 기간이 남았을 때는 「등록해 주세요」, 사흘 남았으면 「지금 등록하지
   * 않으면 서비스가 멈춥니다」가 사실에 맞는 안내다.
   */
  const daysLeft = data.trial.active ? data.trial.daysLeft : null;
  const urgent = daysLeft !== null && daysLeft <= 7;

  return (
    <Alert
      type={urgent ? 'error' : 'warning'}
      showIcon
      style={{ marginBottom: 16 }}
      message={
        urgent ? (
          <Text strong>
            무료 사용 기간이{' '}
            <Text strong style={{ color: '#cf1322' }}>
              {daysLeft === 0 ? '오늘' : `${daysLeft}일 뒤`}
            </Text>{' '}
            끝납니다 — {missing.map((m) => m.what).join(' · ')}을(를) 등록해 주세요
          </Text>
        ) : (
          <Text strong>{missing.map((m) => m.what).join(' · ')}이(가) 등록되지 않았습니다</Text>
        )
      }
      description={
        <Space direction="vertical" size={6} style={{ width: '100%' }}>
          {missing.map((m) => (
            <div key={m.what}>
              <Text>
                <b>{m.what}</b> — {m.where}
              </Text>
              <Button
                type="link"
                size="small"
                style={{ paddingInline: 6 }}
                onClick={() => navigate(m.to)}
              >
                바로 가기
              </Button>
            </div>
          ))}
          {!data.bizLicenseAttached && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              사업자등록증이 없으면 <b>세금계산서를 발행해 드릴 수 없습니다.</b> 올리시면
              기업 정보(상호·대표자·업태·종목·주소)를 자동으로 읽어 채워 드립니다.
            </Text>
          )}
          {!data.payMethodRegistered && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              결제 수단을 등록하지 않으시면 무료 기간이 끝난 뒤{' '}
              <b>서비스 이용이 중지됩니다.</b> 지금은 계좌이체만 가능하며, 등록 화면에서
              입금 계좌와 청구 일정을 함께 확인하실 수 있습니다.
            </Text>
          )}
        </Space>
      }
    />
  );
}
